import React from 'react';
import { Map } from 'immutable';

import { CustomerSettingsForm } from './CustomerSettingsForm/CustomerSettingsForm';
import CustomerSportStakesForm from './CustomerSportStakesForm/CustomerSportStakesForm.jsx';

import Loading from 'src/components/Loading/Loading';
import { Popup } from 'src/components/Popup/Popup';
import addError from 'src/utils/error.jsx';

interface CustomerSettingsTabProps {
    id: number;
    status?: string;
    errors?: Map<string, any>; // todo: type this properly
    customer?: Map<string, any>; // todo: type this properly
    referal?: Map<string, any>; // todo: type this properly
    currency?: Map<string, any>; // todo: type this properly
    languages?: Map<string, any>; // todo: type this properly
    closureCodes?: boolean;
    onUpdateRequest: (id: number, data: any) => void; // todo: type this properly
    onLangRequest: () => void;
    onCheckCurrencyValidation: (customerId: number) => void;
    onCheckReferalValidation: (customerId: number) => void;
}

interface CustomerSettingsTabState {
    customer: any; // todo: type this properly
    isStakeSportOpen: boolean;
    data?: any; // todo: type this properly
}

interface SportStakeData {
    [key: string]: string | number;
}

interface StakeFactorSport {
    sport: string;
    sf: number;
}

const UPDATED_FIELDS = [
    'ageVerification',
    'affordability',
    'betReferralEnabled',
    'bogLimit',
    'bpEnabled',
    'cancelWithdrawal',
    'cashoutEnabled',
    'chatEnabled',
    'commission',
    'contactPreferences',
    'closureReasonId',
    'countryClub',
    'creditAccount',
    'creditLimit',
    'creditPaymentTerms',
    'currency',
    'dragonCircle',
    'failedLoginAttempts',
    'isHVC',
    'isSPOnly',
    'kycStatus',
    'kycWatchlist',
    'language',
    'nextReviewDate',
    'printedStatement',
    'promoBanned',
    'quickBetReferralEnabled',
    'rewardZone',
    'reviewNetLosses',
    'reviewTotalDeposits',
    'ringFencedFunds',
    'securityNotes',
    'spOnlySports',
    'stakeFactor',
    'stakeFactorInPlay',
    'statementFrequency',
    'status',
    'tartanClub',
    'tradingNotes',
    'promoID',
    'incomeaccess',
    'amlRisk',
];

class CustomerSettingsTab extends React.PureComponent<CustomerSettingsTabProps, CustomerSettingsTabState> {
    private allowUpdate: boolean = false;

    static defaultProps = {
        errors: Map(),
        customer: Map(),
        languages: Map(),
    };

    constructor(props: CustomerSettingsTabProps, context?: any) {
        super(props, context);
        this.state = this.createState(props);
    }

    componentDidUpdate(prevProps: CustomerSettingsTabProps) {
        const { status, currency, referal } = this.props;

        // Update the inner state only when an error occurred during request
        if (status !== prevProps.status && (status === 'ERROR' || status === 'FINISHED')) {
            this.setState(this.createState(this.props));
        }

        // Allow form save after currency change validation
        if (currency?.get('state') === 'FINISH' && referal?.get('state') === 'ERROR' && !this.allowUpdate) {
            this.allowUpdate = true;
            this.update();
        }
    }

    componentDidMount() {
        this.props.onLangRequest();
    }

    createState(props: CustomerSettingsTabProps): CustomerSettingsTabState {
        const customer = (props.customer?.get('data') && props.customer.get('data').toJS()) || {};

        if (typeof customer.creditLimit === 'object' && customer.creditLimit.hasOwnProperty('value')) {
            customer.creditLimit.value = customer.creditLimit.value / 100;
        } else {
            customer.creditLimit = customer.creditLimit / 100;
        }

        if (
            customer.hasOwnProperty('bogLimit') &&
            (customer.bogLimit !== null || customer.bogLimit !== undefined || customer.bogLimit !== 0)
        ) {
            customer.bogLimit = customer.bogLimit / 100;
        }

        if (customer.hasOwnProperty('bogLimit') && customer.bogLimit === 0) {
            customer.bogLimit = null;
        }

        if (customer.hasOwnProperty('kycWatchlist') && customer.kycWatchlist !== null) {
            customer.pep = customer.kycWatchlist.value?.pep;
            customer.sanctions = customer.kycWatchlist.value?.sanctions;
        }

        return {
            customer,
            isStakeSportOpen:
                this.state && this.state.hasOwnProperty('isStakeSportOpen') ? this.state.isStakeSportOpen : false,
        };
    }
    //@ts-expect-error
    update(submitData?) {
        if (submitData !== undefined && (submitData.hasOwnProperty('pep') || submitData.hasOwnProperty('sanctions'))) {
            const { pep = false, sanctions = false } = submitData;
            submitData.kycWatchlist = { ...submitData.kycWatchlist, pep, sanctions };
        }
        const data = submitData || this.state.data;
        const { onCheckCurrencyValidation, onCheckReferalValidation, customer } = this.props;
        const { id: customerId } = this.state.customer;
        const dataCopy = Object.assign({}, data);

        if (submitData) {
            dataCopy.contactPreferences = ['sms', 'email', 'phone', 'whatsApp'].filter((key) => {
                return dataCopy[`contactPreferences.${key}`];
            });
        }

        if (submitData) {
            const spOnlySports = submitData.spOnlySports ?? [];
            dataCopy.spOnlySports = ['horseracing', 'internationalhorseracing', 'greyhoundracing'].filter((elem) => {
                if (Array.isArray(spOnlySports) && spOnlySports.includes(elem)) {
                    return submitData[`spOnly-${elem}`] === false ? false : true;
                }
                return submitData[`spOnly-${elem}`];
            });
        }

        Object.keys(dataCopy).forEach((key) => {
            if (!UPDATED_FIELDS.includes(key)) {
                delete dataCopy[key];
            }
        });

        dataCopy.creditLimit = dataCopy.creditLimit * 100;

        if (
            dataCopy.hasOwnProperty('bogLimit') &&
            (dataCopy.bogLimit !== null || dataCopy.bogLimit !== undefined || dataCopy.bogLimit !== 0)
        ) {
            dataCopy.bogLimit = dataCopy.bogLimit * 100;
        }

        if (dataCopy.hasOwnProperty('bogLimit') && dataCopy.bogLimit === 0) {
            dataCopy.bogLimit = null;
        }

        // Validate currency change
        let isChanged;
        const currency = customer?.getIn(['data', 'currency', 'value']) || customer?.getIn(['data', 'currency']);
        const newCurrency =
            dataCopy.currency && dataCopy.currency['value'] ? dataCopy.currency.value : dataCopy.currency;

        // Currency has changed
        // Start validations
        isChanged = currency !== newCurrency;
        if (isChanged && !this.allowUpdate) {
            onCheckCurrencyValidation(customerId);
            onCheckReferalValidation(customerId);

            this.allowUpdate = false;
        }

        if (this.allowUpdate || !isChanged) {
            if (
                this.props.closureCodes === true &&
                (dataCopy.closureReasonId === null || dataCopy.closureReasonId === undefined) &&
                dataCopy.status === 'closed'
            ) {
                addError('User not updated: select Closure reason');
                return;
            }

            this.props.onUpdateRequest(customerId, dataCopy);
        }

        this.setState({
            data: dataCopy,
        });
    }

    updateSportStakes(data: SportStakeData) {
        const stakeFactorSports: StakeFactorSport[] = [];
        const stakeFactorInPlaySports: StakeFactorSport[] = [];

        // Group sport stakes by prematch and inplay and get only the sport name from object key
        Object.keys(data).forEach((key) => {
            if (key.includes('prematch-')) {
                stakeFactorSports.push({
                    sport: key.substr('prematch-'.length),
                    //@ts-expect-error
                    sf: parseFloat(data[key]),
                });
            } else if (key.includes('inplay-')) {
                stakeFactorInPlaySports.push({
                    sport: key.substr('inplay-'.length),
                    //@ts-expect-error
                    sf: parseFloat(data[key]),
                });
            }
        });

        this.props.onUpdateRequest(this.state.customer.id, {
            stakeFactorSports,
            stakeFactorInPlaySports,
        });
    }

    openSportsStakes() {
        this.setState({ isStakeSportOpen: true });
    }

    closeSportsModal() {
        this.setState({ isStakeSportOpen: false });
    }

    render() {
        const { status, errors, languages } = this.props;
        const { customer } = this.state;
        const { stakeFactorSports, stakeFactorInPlaySports } = customer;
        const stakesCount = {
            prematch: stakeFactorSports ? stakeFactorSports.length : 0,
            inplay: stakeFactorInPlaySports ? stakeFactorInPlaySports.length : 0,
        };

        return (
            <div>
                {/* CustomerSettingsForm is HOC that injects props from context */}
                {/* @ts-expect-error */}
                <CustomerSettingsForm
                    data={customer}
                    errors={errors || Map()}
                    onSubmit={this.update.bind(this)}
                    openSportsStakes={this.openSportsStakes.bind(this)}
                    stakesCount={stakesCount}
                    languages={languages}
                />

                <Popup
                    title='Sports Stake'
                    className='customer-sports-stakes-modal'
                    priority={2000}
                    active={this.state.isStakeSportOpen}
                    onClose={this.closeSportsModal.bind(this)}
                >
                    <CustomerSportStakesForm
                        data={{
                            stakeFactorSports: stakeFactorSports || [],
                            stakeFactorInPlaySports: stakeFactorInPlaySports || [],
                        }}
                        updateSportStakes={this.updateSportStakes.bind(this)}
                        status={status}
                    />
                </Popup>
                {!this.state.isStakeSportOpen ? (
                    <Loading
                        active={status === 'IN_PROGRESS'}
                        message='Updating customer'
                    />
                ) : null}
            </div>
        );
    }
}

export default CustomerSettingsTab;
